"use client";

import React, { useState, useEffect } from 'react';
import { Box, Flex, Text, Spinner, Alert, AlertIcon } from '@chakra-ui/react';
import ChartGrid from './ChartGrid';
import DataPoint from './DataPoint';
import TooltipCard from './TooltipCard';
import { appAnalyticsService, ChartDataPoint, AnalyticsParams } from '../../../services/appAnalyticsService';

// Define types
type MetricKey = "SOP Deviation" | "Deal Sentiment" | "Call Quality";
type Metrics = Record<MetricKey, number>;

interface ChartVisualizationProps {
  selectedMetrics?: string[];
  effectiveBusinessId?: string;
  effectiveProjectId?: string;
}

const ChartVisualization: React.FC<ChartVisualizationProps> = ({ 
  selectedMetrics = ["SOP Deviation", "Deal Sentiment"],
  effectiveBusinessId,
  effectiveProjectId
}) => {
  const [hoveredPoint, setHoveredPoint] = useState<number | null>(null);
  const [chartData, setChartData] = useState<ChartDataPoint[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const yMetric = selectedMetrics[0] as MetricKey || "SOP Deviation";
  const xMetric = selectedMetrics[1] as MetricKey || "Deal Sentiment";
  
  useEffect(() => {
    const fetchChartData = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const params: AnalyticsParams = {
          businessId: effectiveBusinessId,
          projectId: effectiveProjectId,
          // Potentially add other params like timeframe if needed by the backend for this chart
        };
        const data = await appAnalyticsService.getProjectComparisonChartData(params);
        setChartData(data);
      } catch (err: any) {
        setError(err.message || "Failed to fetch chart data");
        setChartData([]); // Clear data on error
      } finally {
        setIsLoading(false);
      }
    };

    // Fetch data when component mounts or when business/project IDs change
    // Or when selectedMetrics change if the backend needs to be re-queried (assuming backend handles metric selection)
    fetchChartData();
  }, [effectiveBusinessId, effectiveProjectId, selectedMetrics]);
  
  const handleMouseEnter = (index: number) => {
    setHoveredPoint(index);
  };
  
  const handleMouseLeave = () => {
    setHoveredPoint(null);
  };
  
  const getTooltipPosition = (index: number) => {
    if (!chartData[index]) return { top: '0', left: '0' }; // Guard against undefined point
    const point = chartData[index];
    const xValue = point.metrics[xMetric];
    const leftPos = Math.min(Math.max(xValue - 20, 0), 75);
    
    return {
      top: '10px',
      left: `${leftPos}%`
    };
  };
  
  const getTooltipMetrics = (index: number) => {
    if (!chartData[index]) return []; // Guard against undefined point
    const point = chartData[index];
    return [
      { label: yMetric, value: `${point.metrics[yMetric]}%` },
      { label: xMetric, value: `${point.metrics[xMetric]}%` }
    ];
  };

  if (isLoading) {
    return (
      <Flex justifyContent="center" alignItems="center" h="316px" bg="white" borderRadius="lg" boxShadow="md">
        <Spinner size="xl" />
      </Flex>
    );
  }

  if (error) {
    return (
      <Flex justifyContent="center" alignItems="center" h="316px" bg="white" borderRadius="lg" boxShadow="md" p={4}>
        <Alert status="error" borderRadius="md">
          <AlertIcon />
          <Text fontSize="sm">{error}</Text>
        </Alert>
      </Flex>
    );
  }

  if (chartData.length === 0) {
    return (
       <Flex justifyContent="center" alignItems="center" h="316px" bg="white" borderRadius="lg" boxShadow="md" p={4}>
        <Text color="gray.500">No data available for the selected criteria.</Text>
      </Flex>
    );
  }
  
  return (
    <Box
      position="relative"
      bg="white"
      borderRadius="lg"
      boxShadow="md"
      w="100%"
      h="316px"
      overflow="hidden"
      p={4}
    >
      {/* Chart with axis labels */}
      <Box h="full" position="relative">
        {/* Y-axis labels and title */}
        <Flex 
          position="absolute" 
          left="0"
          top="0" 
          bottom="24px"
          height="calc(100% - 24px)"
          flexDirection="column"
          justifyContent="space-between"
          fontSize="xs"
          width="45px"
          pr={2}
          alignItems="flex-end"
          color="gray.500"
          zIndex="1"
        >
          <Text>100</Text>
          <Text>75</Text>
          <Text>50</Text>
          <Text>25</Text>
          <Text>0</Text>
        </Flex>

        <Text
          position="absolute"
          transform="rotate(-90deg)"
          left="-40px"
          top="50%"
          width="100px"
          textAlign="center"
          fontSize="xs"
          color="gray.600"
          fontWeight="medium"
          zIndex="1"
        >
          {yMetric}
        </Text>

        {/* Chart container */}
        <Box 
          position="relative"
          ml="48px" 
          mr="10px"
          mt="0" 
          mb="30px"
          h="calc(100% - 30px)"
        >
          <ChartGrid />

          {/* Render tooltip for hovered point */}
          {hoveredPoint !== null && chartData[hoveredPoint] && (
            <TooltipCard
              propertyName={chartData[hoveredPoint].name}
              location={chartData[hoveredPoint].location}
              sopDeviation={`${chartData[hoveredPoint].metrics["SOP Deviation"]}%`}
              dealSentiment={`${chartData[hoveredPoint].metrics["Deal Sentiment"]}%`}
              position={getTooltipPosition(hoveredPoint)}
              isVisible={true}
              metrics={getTooltipMetrics(hoveredPoint)}
            />
          )}

          {/* Render data points */}
          {chartData.map((point, index) => (
            <DataPoint
              key={index}
              imageUrl={point.logo}
              position={{ 
                top: `${100 - point.metrics[yMetric]}%`, 
                left: `${point.metrics[xMetric]}%` 
              }}
              onMouseEnter={() => handleMouseEnter(index)}
              onMouseLeave={handleMouseLeave}
            />
          ))}
        </Box>

        {/* X-axis labels */}
        <Flex
          position="absolute"
          bottom="0"
          left="48px"
          right="10px"
          height="24px"
          justifyContent="space-between"
          fontSize="xs"
          color="gray.500"
          zIndex="1"
        >
          <Text>0</Text>
          <Text>25</Text>
          <Text>50</Text>
          <Text>75</Text>
          <Text>100</Text>
        </Flex>

        {/* X-axis title */}
        <Text
          position="absolute"
          bottom="-9px"
          left="0"
          right="0"
          textAlign="center"
          fontSize="xs"
          color="gray.600"
          fontWeight="medium"
          zIndex="1"
        >
          {xMetric}
        </Text>
      </Box>
    </Box>
  );
};

export default ChartVisualization; 